import { NgClass } from '@angular/common';
import {
  Component,
  effect,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AnalyticsChartData,
  BusinessDetails,
  OrganizationModel
} from '@App/common/models';
import {
  AnalyticsOptionsEnum,
  AnalyticsSocialNetworks,
  AnalyticsTypesEnum
} from '@App/common/enums';
import { Subscription } from 'rxjs';
import { StorageService } from '../../services';

import { AnalyticsTabInterface } from '@App/common/interfaces';
import { CardPackageService } from '@App/modules/cards-packages/services';
import {
  CustomerAnalyticsService,
  EmployerPortalService
} from '@App/modules/choosen-history/services';
import { SplitCamelCasePipe } from '../../../modules/pitch-card-shared/pipes/split-camel-case.pipe';
import { ChartModule } from 'primeng/chart';
import { DatePickerModule } from 'primeng/datepicker';

@Component({
  selector: 'app-analytic-reports',
  templateUrl: './analytic-reports.component.html',
  styleUrls: ['./analytic-reports.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    DatePickerModule,
    ChartModule,
    SplitCamelCasePipe,
    NgClass
  ]
})
export class AnalyticReportsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() product: BusinessDetails | OrganizationModel;
  @Input() maxDateValue: any;

  private cardPackagesService = inject(CardPackageService);
  private storageService = inject(StorageService);
  private employerService = inject(EmployerPortalService);
  private customerAnalyticsService = inject(CustomerAnalyticsService);

  range = signal<Date[]>([]);
  chartType = signal('Shared');

  chartData: AnalyticsChartData;
  chartTabs = signal<AnalyticsTabInterface[]>([]);
  chartDataModel = AnalyticsTypesEnum;
  chartOptions: any;
  $analyticSub = new Subscription();

  constructor() {
    effect(() => {
      this.range();
      this.chartType();

      if (!this.product) {
        this.product = JSON.parse(
          this.storageService.getItem('selectedBusiness')
        );
      }

      this.getPitchCardAnalytics(this.product);
    });
  }

  ngOnInit(): void {
    if (this.product) {
      this.initAnalyticsData();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.product && changes.product.previousValue) {
      this.product = changes.product.previousValue;
    }
  }

  ngOnDestroy() {
    if (this.storageService.getItem('selectedBusiness')) {
      this.storageService.removeItem('selectedBusiness');
    }
    if (this.$analyticSub) {
      this.$analyticSub.unsubscribe();
    }
  }

  initAnalyticsData() {
    const today = new Date();
    const monthAgoStamp = today.setMonth(today.getMonth() - 1);
    const startDate = new Date(today.getFullYear(), today.getMonth(), 1);

    this.range.set([startDate, new Date()]);

    this.chartTabs.set(this.employerService.chartsAllTabsData());

    this.getPitchCardAnalytics(this.product);
  }

  getPitchCardAnalytics(business: BusinessDetails) {
    if (
      this.range().length &&
      this.chartType() &&
      (business.businessId || business.id)
    ) {
      this.chartData = new AnalyticsChartData();
      const activeTab = this.chartTabs().find((t) => t.active === true);
      const [from, to] = this.range();
      const startDate = new Date(new Date(from).setHours(0, 0, 0, 0));
      const endDate = new Date(new Date(to).setHours(23, 59, 59, 999));

      let types: string = '';
      activeTab.columns.map((c) => {
        types = types + (types ? ',' : '') + c.types.join(',');
      });

      const params: any = {
        fromDate: new Date(
          startDate.getTime() + Math.abs(startDate.getTimezoneOffset() * 60000)
        ).toISOString(),
        toDate: new Date(
          endDate.getTime() + Math.abs(endDate.getTimezoneOffset() * 60000)
        ).toISOString(),
        types: types,
        businessId: +business.businessId ? business.businessId : business.id
      };

      this.$analyticSub.add(
        this.customerAnalyticsService
          .getReportAnalytics(params)
          .subscribe((r) => {
            this.storageService.setItem('selectedBusiness', business);
            this.populateChartData(r, activeTab);
          })
      );
    }
  }

  populateChartData(data: any, activeTab: AnalyticsTabInterface) {
    const chartData: AnalyticsChartData = {
      labels: [],
      datasets: [
        {
          label: activeTab.name,
          backgroundColor: '#25AEB4',
          borderColor: '#25AEB4',
          data: []
        }
      ]
    };
    if (!Object.keys(data).length) {
      activeTab.columns.forEach(({ name }) => {
        chartData.labels.push(name);
      });

      this.chartData = chartData;
      return;
    }

    activeTab.columns.map((column, index) => {
      column.types.map((type) => {
        if (AnalyticsOptionsEnum[type] in data) {
          if (AnalyticsSocialNetworks[type] in data) {
            if (!chartData.labels.find((label) => label === column.name)) {
              chartData.labels.push(column.name);
            }
            chartData.datasets[0].data.length !== chartData.labels.length
              ? chartData.datasets[0].data.push(
                  data[AnalyticsOptionsEnum[type]]
                )
              : (chartData.datasets[0].data[
                  chartData.datasets[0].data.length - 1
                ] += data[AnalyticsOptionsEnum[type]]);
          } else {
            chartData.labels.push(column.name);
            chartData.datasets[0].data.push(data[AnalyticsOptionsEnum[type]]);
          }
        }
      });
    });

    this.chartData = chartData;
  }

  getBusinessTypeValue(type: string) {
    return this.cardPackagesService.getBusinessTypeValue(type);
  }

  selectChartTab(index: number, tab: { name: any }) {
    // this.chartTabs().map((t: AnalyticsTabInterface) => {
    //   t.active = false;
    // });
    this.chartTabs.update((tabs) =>
      tabs.map((tab, tabIndex) => ({ ...tab, active: tabIndex === index }))
    );
    // this.chartTabs()[index].active = true;
    this.chartType.set(tab.name);
  }
}
